import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { toast } from "@/components/ui/use-toast"
import { useAsyncEffect } from "@/utils"
import { useState } from "preact/hooks"
import { api_fetch } from "../api/client.tsx"
import { Loader2 } from "lucide-react"

interface QuickBooksDesktopSetting {
  uses_quickbooks_desktop: boolean
}

export function QuickBooksDesktopSettings() {
  const [setting, setSetting] = useState<QuickBooksDesktopSetting | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useAsyncEffect(async () => {
    setIsLoading(true)
    try {
      const res = await api_fetch<QuickBooksDesktopSetting>("/organization/uses-quickbooks-desktop")

      if (res.ok) {
        setSetting(res.value.data)
      } else {
        toast({
          title: "Error",
          description: "Failed to load QuickBooks Desktop settings",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error("Failed to fetch QuickBooks Desktop settings:", error)
      toast({
        title: "Error",
        description: "Failed to load QuickBooks Desktop settings",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }, [])

  async function handleSave() {
    if (!setting) return

    setIsSaving(true)
    try {
      const res = await api_fetch<QuickBooksDesktopSetting>("/organization/uses-quickbooks-desktop", {
        method: "POST",
        body: setting,
      })

      if (res.ok) {
        toast({
          title: "Settings Updated",
          description: "QuickBooks Desktop setting saved successfully.",
        })
        setSetting(res.value.data)
      } else {
        toast({
          title: "Error",
          description: res.error?.message || "Failed to update QuickBooks Desktop settings",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error("Failed to update QuickBooks Desktop settings:", error)
      toast({
        title: "Error",
        description: "Failed to update QuickBooks Desktop settings. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }

  function handleChange(checked: boolean) {
    if (!setting) return
    setSetting({ ...setting, uses_quickbooks_desktop: checked })
  }

  if (isLoading) {
    return <div class="text-gray-500">Loading QuickBooks Desktop settings...</div>
  }

  if (!setting) {
    return <div class="text-gray-500">Failed to load settings</div>
  }

  return (
    <div class="bg-white rounded-lg shadow-sm p-6 space-y-6">
      <h2 class="text-lg font-semibold leading-7 text-gray-900">QuickBooks Desktop</h2>
      <div class="space-y-4">
        <div class="flex items-center space-x-2">
          <Checkbox
            id="quickbooks-desktop"
            checked={setting.uses_quickbooks_desktop}
            onCheckedChange={(checked: any) => handleChange(!!checked)}
          />
          <Label htmlFor="quickbooks-desktop" class="cursor-pointer">
            Uses QuickBooks Desktop
          </Label>
        </div>
        <p class="text-sm text-gray-500">
          When enabled, an "Export IIF" button will be available in the accounting table to export data in QuickBooks
          IIF format for import into QuickBooks Desktop.
        </p>
      </div>

      <div class="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            "Save Settings"
          )}
        </Button>
      </div>
    </div>
  )
}

